import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Switch,
    FormControlLabel,
    Button,
    Box,
    Grid,
    IconButton,
    Chip,
    LinearProgress,
    Avatar,
    Divider,
    Alert
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    CloudUpload as CloudUploadIcon,
    Facebook as FacebookIcon,
    Instagram as InstagramIcon,
    Twitter as TwitterIcon,
    LinkedIn as LinkedInIcon,
    YouTube as YouTubeIcon,
    Language as WebsiteIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../Store/store';
import { storage } from '../../../../firebaseConfig';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import toast from 'react-hot-toast';
import axios from 'axios';

interface SocialMedia {
    type: string;
    url: string;
}

interface MeetWithDietitianData {
    content: string;
    isAvailable: boolean;
    meetURL: string;
    photoURL: string;
    socialMedia: SocialMedia[];
}

export default function MeetWithDietitian() {
    const { accessToken } = useSelector((state: RootState) => state.auth.value);
    const { meetWithDietitianData } = useSelector((state: RootState) => state.settings.value);

    // Form States
    const [formData, setFormData] = useState<MeetWithDietitianData>({
        content: meetWithDietitianData.content,
        isAvailable: meetWithDietitianData.isAvailable,
        meetURL: meetWithDietitianData.meetURL,
        photoURL: meetWithDietitianData.photoURL,
        socialMedia: meetWithDietitianData.socialMedia
    });

    // Image Upload States
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Social Media States
    const [newSocialType, setNewSocialType] = useState('');
    const [newSocialUrl, setNewSocialUrl] = useState('');

    // Loading States
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const TikTokIcon = () => {
        return (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fillRule="evenodd"
                clipRule="evenodd"
                imageRendering="optimizeQuality"
                shapeRendering="geometricPrecision"
                textRendering="geometricPrecision"
                viewBox="0 0 293768 333327"
            >
                <path
                    fill="#26f4ee"
                    d="M204958 0c5369 45832 32829 78170 77253 81022v43471l-287 27V87593c-44424-2850-69965-30183-75333-76015l-47060-1v192819c6791 86790-60835 89368-86703 56462 30342 18977 79608 6642 73766-68039V0h58365zM78515 319644c-26591-5471-50770-21358-64969-44588-34496-56437-3401-148418 96651-157884v54345l-164 27v-40773C17274 145544 7961 245185 33650 286633c9906 15984 26169 27227 44864 33011z"
                />
                <path
                    fill="#fb2c53"
                    d="M218434 11587c3505 29920 15609 55386 35948 70259-27522-10602-43651-34934-47791-70262l11843 3zm63489 82463c3786 804 7734 1348 11844 1611v51530c-25770 2537-48321-5946-74600-21749l4034 88251c0 28460 106 41467-15166 67648-34260 58734-95927 63376-137628 35401 54529 22502 137077-4810 136916-103049v-96320c26279 15803 48830 24286 74600 21748V94050zm-171890 37247c5390-1122 11048-1985 16998-2548v54345c-21666 3569-35427 10222-41862 22528-20267 38754 5827 69491 35017 74111-33931 5638-73721-28750-49999-74111 6434-12304 18180-18959 39846-22528v-51797zm64479-119719h1808-1808z"
                />
                <path d="M206590 11578c5369 45832 30910 73164 75333 76015v51528c-25770 2539-48321-5945-74600-21748v96320c206 125717-135035 135283-173673 72939-25688-41449-16376-141089 76383-155862v52323c-21666 3569-33412 10224-39846 22528-39762 76035 98926 121273 89342-1225V11577l47060 1z" />
            </svg>
        );
    };

    const socialMediaTypes = [
        { type: 'Facebook', icon: FacebookIcon, color: '#1877F2' },
        { type: 'Instagram', icon: InstagramIcon, color: '#E4405F' },
        { type: 'Twitter', icon: TwitterIcon, color: '#1DA1F2' },
        { type: 'LinkedIn', icon: LinkedInIcon, color: '#0A66C2' },
        { type: 'YouTube', icon: YouTubeIcon, color: '#FF0000' },
        { type: 'Website', icon: WebsiteIcon, color: '#666666' },
        { type: 'TikTok', icon: TikTokIcon, color: '#000000' }
    ];

    // Handle form field changes
    const handleInputChange = (field: keyof MeetWithDietitianData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle image upload
    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Please upload only image files!');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File size must be smaller than 5MB!');
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        const storageRef = ref(storage, `dietitian-photos/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
            },
            (error) => {
                console.error('Upload error:', error);
                toast.error('Photo upload error!');
                setUploading(false);
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    handleInputChange('photoURL', downloadURL);
                    toast.success('Photo uploaded successfully!');
                    setUploading(false);
                    setUploadProgress(0);
                });
            }
        );
    };

    // Add social media
    const addSocialMedia = () => {
        if (!newSocialType || !newSocialUrl) {
            toast.error('Please enter social media type and URL!');
            return;
        }

        // Check if type already exists
        if (formData.socialMedia.some(social => social.type === newSocialType)) {
            toast.error('This social media type has already been added!');
            return;
        }

        const newSocial: SocialMedia = {
            type: newSocialType,
            url: newSocialUrl
        };

        setFormData(prev => ({
            ...prev,
            socialMedia: [...prev.socialMedia, newSocial]
        }));

        setNewSocialType('');
        setNewSocialUrl('');
        toast.success('Social media account added!');
    };

    // Remove social media
    const removeSocialMedia = (index: number) => {
        setFormData(prev => ({
            ...prev,
            socialMedia: prev.socialMedia.filter((_, i) => i !== index)
        }));
        toast.success('Social media account removed!');
    };

    // Get social media icon
    const getSocialMediaIcon = (type: string) => {
        const socialType = socialMediaTypes.find(s => s.type === type);
        if (type === 'TikTok') {
            return <TikTokIcon />;
        }
        if (socialType) {
            const IconComponent = socialType.icon;
            return <IconComponent sx={{ color: socialType.color }} />;
        }
        return <WebsiteIcon />;
    };

    // Save data (placeholder for API integration)
    const handleSave = async () => {
        setSaving(true);
        try {
            // TODO: API integration will be added here
            console.log('Saving data:', formData);
            await updateData();
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            toast.success('Data saved successfully!');
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Error occurred while saving!');
        } finally {
            setSaving(false);
        }
    };

    const updateData = async () => {
        await axios.post(
            'https://us-central1-eatwellmamav2.cloudfunctions.net/updateMeetTheDietitian',
            {
                content: formData.content,
                isAvailable: formData.isAvailable,
                meetURL: formData.meetURL,
                photoURL: formData.photoURL,
                socialMedia: formData.socialMedia
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                }
            }
        ).then((response) => {
            console.log("Update Data Response:", response.data);
            toast.success("Data updated successfully!");
        }).catch((error) => {
            console.log("Update Data Error:", error);
            console.log("Update Data Error Message:", error.response.data);
            toast.error("Error updating data");
        });
    };

    // Load data (placeholder for API integration)
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // TODO: API integration will be added here
                console.log('Loading data...');

                // Simulate API call
                await new Promise(resolve => setTimeout(resolve, 500));

                // For now, set some default data
                // setFormData(loadedData);
            } catch (error) {
                console.error('Load error:', error);
                toast.error('Data loading error!');
            } finally {
                setLoading(false);
            }
        };

        if (accessToken) {
            loadData();
        }
    }, [accessToken]);

    if (loading) {
        return (
            <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 3 }}>
                <Typography variant="h5" gutterBottom>Meet with Dietitian - Loading...</Typography>
                <LinearProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Meet with Dietitian
            </Typography>

            <Card elevation={3}>
                <CardContent sx={{ p: 4 }}>
                    {/* Availability Toggle */}
                    <Box sx={{ mb: 4 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isAvailable}
                                    onChange={(e) => handleInputChange('isAvailable', e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="h6">
                                    {formData.isAvailable ? 'Appointment System Active' : 'Appointment System Closed'}
                                </Typography>
                            }
                        />
                        {!formData.isAvailable && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                When the appointment system is closed, users will not be able to make appointments.
                            </Alert>
                        )}
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Photo Upload Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Dietitian Photo
                        </Typography>

                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Avatar
                                        src={formData.photoURL}
                                        sx={{
                                            width: 150,
                                            height: 150,
                                            mx: 'auto',
                                            mb: 2,
                                            border: '3px solid #e0e0e0'
                                        }}
                                    />
                                    {formData.photoURL && (
                                        <Typography variant="body2" color="text.secondary">
                                            Current Photo
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={8}>
                                <TextField
                                    type="file"
                                    inputProps={{ accept: 'image/*' }}
                                    onChange={handleImageUpload}
                                    disabled={uploading}
                                    fullWidth
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                />

                                {uploading && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Photo uploading... {Math.round(uploadProgress)}%
                                        </Typography>
                                        <LinearProgress variant="determinate" value={uploadProgress} />
                                    </Box>
                                )}

                                <Button
                                    variant="outlined"
                                    startIcon={<CloudUploadIcon />}
                                    component="label"
                                    disabled={uploading}
                                    fullWidth
                                >
                                    {uploading ? 'Uploading...' : 'Select Photo'}
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                    />
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Content Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Content
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            variant="outlined"
                            placeholder="Description about dietitian, experience, expertise areas, etc..."
                            value={formData.content}
                            onChange={(e) => handleInputChange('content', e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {formData.content.length} characters
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Meeting URL Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Appointment URL
                        </Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="https://calendly.com/your-link or similar appointment link"
                            value={formData.meetURL}
                            onChange={(e) => handleInputChange('meetURL', e.target.value)}
                            helperText="Users will be redirected to this link to make appointments"
                        />
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Social Media Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Social Media Accounts
                        </Typography>

                        {/* Add New Social Media */}
                        <Box sx={{ mb: 3, p: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Add New Social Media Account
                            </Typography>

                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        select
                                        fullWidth
                                        value={newSocialType}
                                        onChange={(e) => setNewSocialType(e.target.value)}
                                        SelectProps={{
                                            native: true,
                                        }}
                                    >
                                        <option value="">Select Platform</option>
                                        {socialMediaTypes.map((social) => (
                                            <option key={social.type} value={social.type}>
                                                {social.type}
                                            </option>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="URL"
                                        placeholder="https://..."
                                        value={newSocialUrl}
                                        onChange={(e) => setNewSocialUrl(e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={addSocialMedia}
                                        sx={{ height: 56 }}
                                    >
                                        Add
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Existing Social Media List */}
                        <Box>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Current Social Media Accounts ({formData.socialMedia.length})
                            </Typography>

                            {formData.socialMedia.length === 0 ? (
                                <Alert severity="info">
                                    No social media accounts have been added yet.
                                </Alert>
                            ) : (
                                <Grid container spacing={2}>
                                    {formData.socialMedia.map((social, index) => (
                                        <Grid item xs={12} md={6} key={index}>
                                            <Card variant="outlined" sx={{ p: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {getSocialMediaIcon(social.type)}
                                                        <Box>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                                {social.type}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    maxWidth: 200,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {social.url}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => removeSocialMedia(index)}
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Save Button */}
                    <Box sx={{ textAlign: 'center' }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleSave}
                            disabled={saving || uploading}
                            sx={{
                                minWidth: 200,
                                height: 48,
                                fontSize: '1.1rem'
                            }}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
